import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { offlineDB } from '@/lib/offlineDB';
import { sitesApi, entitiesApi, subCategoriesApi, entriesApi } from '@/lib/api';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncQueueCount, setSyncQueueCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const syncIntervalRef = useRef(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingActions();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load queue count on mount
  useEffect(() => {
    refreshQueueCount();
    offlineDB.meta.get('lastSyncTime').then(t => setLastSyncTime(t)).catch(() => {});
  }, []);

  // Periodic sync check when online
  useEffect(() => {
    if (isOnline) {
      syncIntervalRef.current = setInterval(() => {
        refreshQueueCount();
      }, 30000); // Check queue every 30s
    }
    return () => {
      if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
    };
  }, [isOnline]);

  const refreshQueueCount = async () => {
    try {
      const count = await offlineDB.syncQueue.count();
      setSyncQueueCount(count);
    } catch {}
  };

  // Cache reference data locally when online
  const cacheReferenceData = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      const [sitesRes, entitiesRes, subCatsRes] = await Promise.allSettled([
        sitesApi.list(),
        entitiesApi.list(),
        subCategoriesApi.list(),
      ]);

      if (sitesRes.status === 'fulfilled') await offlineDB.sites.save(sitesRes.value.data);
      if (entitiesRes.status === 'fulfilled') await offlineDB.entities.save(entitiesRes.value.data);
      if (subCatsRes.status === 'fulfilled') await offlineDB.subCategories.save(subCatsRes.value.data);

      const now = Date.now();
      await offlineDB.meta.set('lastSyncTime', now);
      setLastSyncTime(now);
    } catch {}
  }, []);

  // Queue an action for offline sync
  const queueAction = useCallback(async (action) => {
    await offlineDB.syncQueue.add(action);
    await refreshQueueCount();
  }, []);

  // Replay queued actions when back online
  const syncPendingActions = useCallback(async () => {
    if (syncing || !navigator.onLine) return;
    setSyncing(true);
    try {
      const queue = await offlineDB.syncQueue.getAll();
      if (queue.length === 0) { setSyncing(false); return; }

      // Sort by timestamp to maintain order
      queue.sort((a, b) => a.timestamp - b.timestamp);

      for (const item of queue) {
        try {
          switch (item.type) {
            case 'CREATE_ENTRY':
              await entriesApi.create(item.payload);
              break;
            case 'UPDATE_ENTRY':
              await entriesApi.update(item.targetId, item.payload);
              break;
            case 'DELETE_ENTRY':
              await entriesApi.delete(item.targetId);
              break;
            case 'BULK_UPLOAD':
              await entriesApi.bulkUpload(item.payload);
              break;
            default:
              break;
          }
          await offlineDB.syncQueue.remove(item.id);
        } catch (err) {
          // If 4xx error, remove from queue (invalid data)
          if (err?.response?.status >= 400 && err?.response?.status < 500) {
            await offlineDB.syncQueue.remove(item.id);
          }
          // 5xx or network error: leave in queue for retry
        }
      }
      await refreshQueueCount();
    } catch {}
    setSyncing(false);
  }, [syncing]);

  // Get cached data for offline use
  const getCachedSites = useCallback(() => offlineDB.sites.getAll(), []);
  const getCachedEntities = useCallback(() => offlineDB.entities.getAll(), []);
  const getCachedSubCategories = useCallback(() => offlineDB.subCategories.getAll(), []);

  return (
    <OfflineContext.Provider value={{
      isOnline,
      syncQueueCount,
      syncing,
      lastSyncTime,
      queueAction,
      syncPendingActions,
      cacheReferenceData,
      getCachedSites,
      getCachedEntities,
      getCachedSubCategories,
      refreshQueueCount,
    }}>
      {children}
    </OfflineContext.Provider>
  );
}

export const useOffline = () => useContext(OfflineContext);
